import { toBase64 } from "@foreman/protocol";
import type { Connector } from "./remote-client";

/**
 * Connector over the standard `WebSocket` global, so the same code runs in browsers, React Native
 * and Node 22. (Node tests can also use the `ws`-based connector in apps/daemon.)
 *
 * Browsers cannot observe WebSocket pings, so liveness comes from the relay's keepalive text
 * frames (and any other traffic): if nothing arrives for `livenessTimeoutMs`, the path is dead
 * and the socket is closed, which makes RemoteClient reconnect and replay.
 */
export function webSocketConnector(
  relayUrl: string,
  daemonPublicKey: Uint8Array,
  opts: { livenessTimeoutMs?: number; /** The sign-in or guest token the relay identifies this phone with, read at every (re)connect. */ identity?: () => string | null } = {},
): Connector {
  const liveness = opts.livenessTimeoutMs ?? 75_000;
  return (h) => {
    const token = opts.identity?.() ?? null;
    const ws = new WebSocket(`${relayUrl}/phone?daemon=${encodeURIComponent(toBase64(daemonPublicKey))}${token ? `&t=${encodeURIComponent(token)}` : ""}`);
    ws.binaryType = "arraybuffer";
    let timer: ReturnType<typeof setTimeout> | null = null;
    const touch = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => ws.close(), liveness);
    };
    touch();
    ws.onopen = () => {
      touch();
      h.onOpen();
    };
    ws.onmessage = (ev) => {
      touch();
      if (typeof ev.data === "string") return; // keepalive
      h.onMessage(new Uint8Array(ev.data as ArrayBuffer));
    };
    ws.onerror = () => {}; // 'close' always follows
    ws.onclose = (ev) => {
      if (timer) clearTimeout(timer);
      h.onClose(ev.code, ev.reason);
    };
    return {
      send: (data) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(data as BufferSource);
      },
      close: (code, reason) => {
        // The standard API only allows 1000 and 3000-4999 as close codes.
        if (ws.readyState === WebSocket.CONNECTING) ws.close();
        else ws.close(code === 1000 || (code !== undefined && code >= 3000) ? code : 1000, reason);
      },
    };
  };
}
